import React, { useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RevAlert, RevBarra, RevButton, RevCard, RevField, RevProgress } from '../../../components/ds';
import Modal from '../../../components/generic/modal';
import { createSponsorId, inspectSponsorFile, MAX_SPONSORS, moveSponsor, sponsorErrors, validSponsors } from '../../../helpers/eventSponsors';
import { createSaveAttempt, getEventSponsors, runSaveAttempt } from '../../../services/eventSponsorsService';

const editableSponsors = sponsors => sponsors.map(sponsor => ({ ...sponsor, websiteUrl: sponsor.websiteUrl || '' }));

// Editor de patrocinadores, sección de la pestaña Galería pública (eventPublicGallery.js),
// que es quien comprueba que el usuario sea el dueño antes de montarlo.
export const SponsorsEditor = ({ eventId }) => {
    const { t } = useTranslation();
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [inspecting, setInspecting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [fileErrors, setFileErrors] = useState({});
    const [reloadOpen, setReloadOpen] = useState(false);
    const [progress, setProgress] = useState({ completed: 0, total: 1 });
    const attempt = useRef(null);
    const mounted = useRef(true);
    const busyRef = useRef(false);
    // Compare metadata only: serializing up to twenty base64 logos on every keystroke
    // would allocate tens of megabytes. A selected local image is always a change.
    const dirty = saved !== null && (draft.length !== saved.sponsors.length || draft.some((sponsor, index) => {
        const original = saved.sponsors[index];
        return sponsor.imageBase64 || sponsor.id !== original?.id || sponsor.name !== original?.name
            || sponsor.websiteUrl !== (original?.websiteUrl || '') || sponsor.logoUrl !== original?.logoUrl;
    }));
    const busy = saving || inspecting;

    const adopt = (state) => {
        setSaved(state);
        setDraft(editableSponsors(state.sponsors));
        setFileErrors({});
        setShowErrors(false);
        attempt.current = null;
    };

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const state = await getEventSponsors(eventId);
            if (mounted.current) adopt(state);
        } catch {
            if (mounted.current) setError('load');
        } finally {
            if (mounted.current) setLoading(false);
        }
    };

    useEffect(() => {
        mounted.current = true;
        load();
        return () => { mounted.current = false; };
    }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!dirty && !saving) return undefined;
        const warn = event => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty, saving]);

    const changeDraft = next => {
        setDraft(next);
        attempt.current = null;
        setSuccess(false);
        setError(null);
    };
    const update = (id, values) => changeDraft(draft.map(sponsor => sponsor.id === id ? { ...sponsor, ...values } : sponsor));
    const selectLogo = async (id, file) => {
        if (!file || busyRef.current) return;
        busyRef.current = true;
        setInspecting(true);
        setFileErrors(current => ({ ...current, [id]: null }));
        try {
            const image = await inspectSponsorFile(file);
            if (!mounted.current) return;
            // Selection is local; no API call occurs before Save changes.
            setDraft(current => current.map(sponsor => sponsor.id === id ? { ...sponsor, ...image } : sponsor));
            attempt.current = null;
            setSuccess(false);
            setError(null);
        } catch (failure) {
            if (mounted.current) setFileErrors(current => ({ ...current, [id]: failure.message }));
        } finally {
            busyRef.current = false;
            if (mounted.current) setInspecting(false);
        }
    };

    const save = async () => {
        if (busyRef.current || !dirty) return;
        setShowErrors(true);
        if (!validSponsors(draft)) return;
        busyRef.current = true;
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            if (!attempt.current) attempt.current = createSaveAttempt(saved.revision, createSponsorId(), draft);
            const state = await runSaveAttempt(eventId, attempt.current, setProgress);
            if (mounted.current) { adopt(state); setSuccess(true); }
        } catch (failure) {
            if (!mounted.current) return;
            const status = failure.response?.status;
            setError(status === 409 ? 'conflict' : status === 410 ? 'expired' : status === 403 ? 'forbidden' : 'save');
            // A definitive expiry needs fresh uploads and a new immutable save attempt.
            if (status === 410) attempt.current = null;
        } finally {
            busyRef.current = false;
            if (mounted.current) setSaving(false);
        }
    };

    const cancel = () => {
        adopt(saved);
        setError(null);
        setSuccess(false);
    };

    const progressLabel = progress.publishing
        ? t('eventSponsors.progress.publishing')
        : t('eventSponsors.progress.uploading', { current: progress.completed + 1, total: Math.max(1, progress.total - 1) });

    return <div className="rev-sponsors">
        <Prompt when={dirty || saving} message={() => saving ? false : t('eventSponsors.unsaved')} />
        <RevCard title={t('eventSponsors.title')} sub={t('eventSponsors.intro')}>
            {loading && <div role="status"><RevProgress aria-label={t('eventSponsors.loading')} /><span className="rev-help">{t('eventSponsors.loading')}</span></div>}
            {error && <RevAlert tone="err" role="alert" actions={!busy && ['load', 'conflict'].includes(error)
                ? <RevButton onClick={() => dirty ? setReloadOpen(true) : load()}>{t(error === 'load' ? 'eventSponsors.retry' : 'eventSponsors.reload')}</RevButton>
                : undefined}>{t(`eventSponsors.error.${error}`)}</RevAlert>}
            {!loading && saved !== null && <>
                <div className="rev-sponsors__toolbar">
                    <span className="rev-help">{t('eventSponsors.count', { count: draft.length, max: MAX_SPONSORS })}</span>
                    <RevButton variant="secondary" disabled={busy || draft.length >= MAX_SPONSORS}
                        onClick={() => changeDraft([...draft, { id: createSponsorId(), name: '', websiteUrl: '' }])}>
                        {t('eventSponsors.add')}
                    </RevButton>
                </div>
                <ol className="rev-sponsors__list">
                    {draft.map((sponsor, index) => {
                        const errors = sponsorErrors(sponsor);
                        return <li key={sponsor.id} className="rev-sponsors__row">
                            <div className="rev-sponsors__row-head">
                                <h3 className="rev-sponsors__row-title">{t('eventSponsors.entry', { number: index + 1 })}</h3>
                                <div className="rev-sponsors__actions">
                                    <RevButton size="sm" disabled={busy || index === 0} aria-label={t('eventSponsors.moveUp', { number: index + 1 })} onClick={() => changeDraft(moveSponsor(draft, index, -1))}>{t('eventSponsors.up')}</RevButton>
                                    <RevButton size="sm" disabled={busy || index === draft.length - 1} aria-label={t('eventSponsors.moveDown', { number: index + 1 })} onClick={() => changeDraft(moveSponsor(draft, index, 1))}>{t('eventSponsors.down')}</RevButton>
                                    <RevButton size="sm" variant="danger" disabled={busy} aria-label={t('eventSponsors.removeEntry', { number: index + 1 })} onClick={() => changeDraft(draft.filter(item => item.id !== sponsor.id))}>{t('eventSponsors.remove')}</RevButton>
                                </div>
                            </div>
                            <div className="rev-sponsors__fields">
                                <RevField label={t('eventSponsors.logo')} htmlFor={`logo-${sponsor.id}`}
                                    error={fileErrors[sponsor.id] ? t(`eventSponsors.validation.${fileErrors[sponsor.id]}`) : showErrors && errors.logo ? t('eventSponsors.validation.logo') : undefined}>
                                    <input id={`logo-${sponsor.id}`} type="file" className="rev-sponsors__file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" disabled={busy}
                                        onChange={event => { selectLogo(sponsor.id, event.target.files?.[0]); event.target.value = ''; }} />
                                </RevField>
                                <RevField label={t('eventSponsors.name')} htmlFor={`name-${sponsor.id}`} error={showErrors && errors.name ? t('eventSponsors.validation.name') : undefined}>
                                    <input id={`name-${sponsor.id}`} className="rev-input" value={sponsor.name} maxLength={100} disabled={busy} required
                                        aria-invalid={showErrors && !!errors.name} placeholder={t('eventSponsors.nameExample')} onChange={event => update(sponsor.id, { name: event.target.value })} />
                                </RevField>
                                <RevField label={t('eventSponsors.website')} htmlFor={`website-${sponsor.id}`}
                                    error={showErrors && errors.websiteUrl ? t('eventSponsors.validation.website') : undefined}>
                                    <input id={`website-${sponsor.id}`} className="rev-input" type="url" value={sponsor.websiteUrl} disabled={busy}
                                        aria-invalid={showErrors && !!errors.websiteUrl} placeholder="https://nike.com" onChange={event => update(sponsor.id, { websiteUrl: event.target.value })} />
                                </RevField>
                            </div>
                        </li>;
                    })}
                </ol>
                <div className="rev-sponsors__preview-block">
                    <div className="rev-sponsors__preview-head">
                        <h3 className="rev-sponsors__row-title">{t('eventSponsors.preview')}</h3>
                        <span className="rev-help">{t('eventSponsors.previewHelp')}</span>
                    </div>
                    <section className="rev-sponsors__preview" data-register="dark" aria-label={t('eventSponsors.preview')}>
                        <h3 className="rev-sponsors__eyebrow">{t('eventSponsors.sponsoredBy')}</h3>
                        {draft.length ? <div className="rev-sponsors__grid">
                            {draft.map(sponsor => <div className="rev-sponsors__tile" key={sponsor.id}>
                                <div className="rev-sponsors__logo">
                                    {sponsor.previewUrl || sponsor.logoUrl ? <img src={sponsor.previewUrl || sponsor.logoUrl} alt="" /> : <span>{t('eventSponsors.logoPlaceholder')}</span>}
                                </div>
                                <span className="rev-sponsors__caption">{sponsor.name || t('eventSponsors.namePlaceholder')}</span>
                            </div>)}
                        </div> : <p className="rev-sponsors__caption">{t('eventSponsors.previewEmpty')}</p>}
                    </section>
                </div>
            </>}
        </RevCard>
        {!loading && saved !== null && <RevBarra sangre ok={success ? t('eventSponsors.saved') : undefined}
            left={saving ? <div className="rev-sponsors__progress" role="status"><span>{progressLabel}</span><RevProgress value={progress.completed} max={progress.total} aria-label={progressLabel} /></div>
                : inspecting ? <span role="status">{t('eventSponsors.inspecting')}</span> : dirty ? <span className="rev-help">{t('eventSponsors.pending')}</span> : undefined}>
            <RevButton onClick={cancel} disabled={busy || !dirty}>{t('eventSponsors.cancel')}</RevButton>
            <RevButton variant="primary" onClick={save} loading={saving} disabled={busy || !dirty}>{t('eventSponsors.save')}</RevButton>
        </RevBarra>}
        {reloadOpen && <Modal title={t('eventSponsors.reloadTitle')} message={t('eventSponsors.reloadBody')}
            showCancelButton showConfirmButton cancelButtonText={t('eventSponsors.keepEditing')} confirmButtonText={t('eventSponsors.reload')}
            onCancel={() => setReloadOpen(false)} onConfirm={() => { setReloadOpen(false); load(); }} />}
    </div>;
};
